import type {StateCreator} from 'zustand'
import type {SuggestionIdentifier, SuggestedChangeLineRange} from '@github-ui/diff-lines/conversation-types'

export type BatchedSuggestion = SuggestionIdentifier & {
  /** Thread ID for conflict detection */
  threadId?: number | null | undefined
  /** Path/line-range pairs to represent suggestions that span multiple files and line ranges */
  pathLineRanges?: Array<{path: string; lineRange: SuggestedChangeLineRange}>
}

export interface SuggestionBatchSlice {
  suggestionBatch: {
    activeBatch: BatchedSuggestion[]
    isSuggestionBatchPending: boolean
  }
  suggestionBatchActions: {
    addSuggestionToBatch: (suggestionId: BatchedSuggestion) => void
    removeSuggestionFromBatch: (suggestionId: BatchedSuggestion) => void
    clearSuggestionBatch: () => void
    loadActiveBatchFromStorage: (activeBatch: BatchedSuggestion[]) => void
  }
}

export type SuggestionBatchSliceOpts = {
  initialActiveBatch?: BatchedSuggestion[]
}

export const createSuggestionBatchSlice =
  (opts: SuggestionBatchSliceOpts): StateCreator<SuggestionBatchSlice, [], [], SuggestionBatchSlice> =>
  set => ({
    suggestionBatch: {
      activeBatch: opts.initialActiveBatch ?? [],
      isSuggestionBatchPending: (opts.initialActiveBatch?.length ?? 0) > 0,
    },
    suggestionBatchActions: {
      addSuggestionToBatch: (suggestionId: BatchedSuggestion) =>
        set(state => {
          const exists = state.suggestionBatch.activeBatch.some(
            id =>
              id.commentDatabaseId === suggestionId.commentDatabaseId &&
              id.suggestionIndex === suggestionId.suggestionIndex,
          )
          if (exists) {
            return state
          }
          const newActiveBatch = [...state.suggestionBatch.activeBatch, suggestionId]

          return {
            suggestionBatch: {
              ...state.suggestionBatch,
              activeBatch: newActiveBatch,
              isSuggestionBatchPending: newActiveBatch.length > 0,
            },
          }
        }),
      removeSuggestionFromBatch: (suggestionId: BatchedSuggestion) =>
        set(state => {
          const newActiveBatch = state.suggestionBatch.activeBatch.filter(
            id =>
              !(
                id.commentDatabaseId === suggestionId.commentDatabaseId &&
                id.suggestionIndex === suggestionId.suggestionIndex
              ),
          )

          return {
            suggestionBatch: {
              ...state.suggestionBatch,
              activeBatch: newActiveBatch,
              isSuggestionBatchPending: newActiveBatch.length > 0,
            },
          }
        }),
      clearSuggestionBatch: () =>
        set(state => {
          return {
            suggestionBatch: {
              ...state.suggestionBatch,
              activeBatch: [],
              isSuggestionBatchPending: false,
            },
          }
        }),
      loadActiveBatchFromStorage: (activeBatch: BatchedSuggestion[]) =>
        set(state => ({
          suggestionBatch: {
            ...state.suggestionBatch,
            activeBatch,
            isSuggestionBatchPending: activeBatch.length > 0,
          },
        })),
    },
  })
